import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAtom } from "jotai";
import { useMutation } from "@tanstack/react-query";
import { Typography } from "@/frontend/shared/ui/Typography";
import { XIcon } from "@/frontend/shared/assets/icons";
import { Input } from "@/frontend/shared/ui/Input";
import { SelectionDropdown } from "@/frontend/shared/ui/SelectionDropdown";
import { Textarea } from "@/frontend/shared/ui/Textarea/Textarea";
import { deutscheTelekomAtom, type ApplicationForm } from "@/frontend/atoms/application-form/deutsche-telekom";
import { NormalButton } from "@/frontend/shared/ui/Button";
import { submitApplicationForm } from "../model/services/submitApplicationForm";

import styles from "./ApplicationFormDeutscheTelekom.module.scss";
import { getRouteSignIn } from "@/frontend/shared/const/router";

function convertFilesToBase64(files: File[]): Promise<{ filename: string; base64: string; filesize: string }[]> {
  const filePromises = files.map((file) => {
    return new Promise<{ filename: string; base64: string; filesize: string }>((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        let filesize: string;
        if (file.size >= 1024 * 1024) {
          filesize = `${(file.size / (1024 * 1024)).toFixed(2)} MB`;
        } else if (file.size >= 1024) {
          filesize = `${(file.size / 1024).toFixed(2)} KB`;
        } else {
          filesize = `${file.size} bytes`;
        }

        resolve({
          filename: file.name,
          base64: reader.result as string,
          filesize: filesize
        });
      };

      reader.onerror = () => {
        reject(new Error(`Error reading file: ${file.name}`));
      };

      reader.readAsDataURL(file);
    });
  });

  return Promise.all(filePromises);
}

export const ApplicationFormDeutscheTelekom = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useAtom(deutscheTelekomAtom);

  const [isSa5gOpen, setIsSa5gOpen] = useState<boolean>(false);
  const [isTestWithDataSentricOpen, setIsTestWithDataSentricOpen] = useState<boolean>(false);
  const [isExternalCloud, setIsExternalCloud] = useState<boolean>(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const mutation = useMutation({
    mutationKey: ["application-form/deutsche-telekom"],
    mutationFn: async (data: Omit<ApplicationForm, "files"> & { files: { filename: string; base64: string }[] }) =>
      await submitApplicationForm(data)
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  function handleButtonClick() {
    fileInputRef.current?.click();
  }

  async function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const _files = Array.from(e.target.files || []);

    try {
      const files = await convertFilesToBase64(_files);
      setFormData((prev) => ({ ...prev, files }));
    } catch (error) {
      console.error("Error handling input change", error);
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  }

  const submitApplication = async () => {
    const { files, ...rest } = formData;
    const payload = { ...rest, files: [] as { filename: string; base64: string }[] };
    payload.files = files.map((f) => ({ filename: f.filename, base64: f.base64 }));

    const response = await mutation.mutateAsync(payload);
    if (response?.signUpUrl) {
      window?.location?.replace?.(response.signUpUrl);
    }
  };

  useEffect(() => {
    const env = import.meta.env.VITE_ENV;
    // Temporarily prevent production access - J
    if (env === "production") {
      navigate(getRouteSignIn());
    }
  }, [navigate]);

  return (
    <div className={styles.container}>
      <div className={styles.subContainerLeft}>
        <img src="/logos/deutsche-telekom.png" alt="Deutsche Telekom" className={styles.logo} />
        <Typography variant="heading-4" color="greyscale900">
          Deutsche Telekom
        </Typography>
        <Typography variant="body-1">Application Submission</Typography>
      </div>

      <div className={styles.subContainerRight}>
        <div className={styles.applicationForm}>
          <Typography variant="heading-4" color="greyscale900" style={{ marginBottom: "1rem" }}>
            Application Form
          </Typography>
          <div className={styles.formRow}>
            <div>
              <Typography className={styles.inputCaption} variant="label-3" color="greyscale700">
                Company Name
              </Typography>
              <Input
                type="text"
                id="companyName"
                name="companyName"
                className={styles.inputField}
                value={formData.companyName}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className={styles.formRow}>
            <div>
              <Typography className={styles.inputCaption} variant="label-3" color="greyscale700">
                Website
              </Typography>
              <Input
                type="text"
                id="website"
                name="website"
                className={styles.inputField}
                value={formData.website}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className={styles.inputRow}>
            <div>
              <Typography className={styles.inputCaption} variant="label-3" color="greyscale700">
                First Name
              </Typography>
              <Input
                type="text"
                id="firstName"
                name="firstName"
                className={styles.inputField}
                value={formData.firstName}
                onChange={handleChange}
              />
            </div>

            <div>
              <Typography className={styles.inputCaption} variant="label-3" color="greyscale700">
                Last Name
              </Typography>
              <Input
                type="text"
                id="lastName"
                name="lastName"
                className={styles.inputField}
                value={formData.lastName}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className={styles.formRow}>
            <div>
              <Typography className={styles.inputCaption} variant="label-3" color="greyscale700">
                Email
              </Typography>
              <Input
                type="text"
                id="email"
                name="email"
                className={styles.inputField}
                value={formData.email}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className={styles.formRow}>
            <div>
              <Typography className={styles.inputCaption} variant="label-3" color="greyscale700">
                Phone Number
              </Typography>
              <Input
                type="text"
                id="phoneNumber"
                name="phoneNumber"
                className={styles.inputField}
                value={formData.phoneNumber}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className={styles.formRow}>
            <div>
              <Typography className={styles.inputCaption} variant="label-3" color="greyscale700">
                Country
              </Typography>
              <Input
                type="text"
                id="country"
                name="country"
                className={styles.inputField}
                value={formData.country}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className={styles.formRow}>
            <div>
              <Typography className={styles.inputCaption} variant="label-3" color="greyscale700">
                Please describe your company in one sentence
              </Typography>
              <Textarea
                rows={5}
                className={styles.inputField}
                id="companyDescription"
                name="companyDescription"
                value={formData.companyDescription}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className={styles.formRow}>
            <div>
              <Typography className={styles.inputCaption} variant="label-3" color="greyscale700">
                What's your current solution? (Describe your product in one sentence)
              </Typography>
              <Textarea
                rows={3}
                className={styles.inputField}
                id="productDescription"
                name="productDescription"
                value={formData.productDescription}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className={styles.formRow}>
            <div>
              <Typography className={styles.inputCaption} variant="label-3" color="greyscale700">
                Company Type
              </Typography>
              <Input
                type="text"
                id="companyType"
                name="companyType"
                className={styles.inputField}
                value={formData.companyType}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className={styles.formRow}>
            <div>
              <Typography className={styles.inputCaption} variant="label-3" color="greyscale700">
                Business Model
              </Typography>
              <Input
                type="text"
                id="businessModel"
                name="businessModel"
                className={styles.inputField}
                value={formData.businessModel}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className={styles.formRow}>
            <div>
              <Typography className={styles.inputCaption} variant="label-3" color="greyscale700">
                Focus Area
              </Typography>
              <Input
                type="text"
                id="focusArea"
                name="focusArea"
                className={styles.inputField}
                value={formData.focusArea}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className={styles.formRow}>
            <div>
              <Typography className={styles.inputCaption} variant="label-3" color="greyscale700">
                What is your core technology? <span className={styles.optionalText}>(optional)</span>
              </Typography>
              <Input
                type="text"
                id="coreTechnology"
                name="coreTechnology"
                className={styles.inputField}
                value={formData.coreTechnology}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className={styles.formRow}>
            <div>
              <Typography className={styles.inputCaption} variant="label-3" color="greyscale700">
                What throughput is required for your solution?
              </Typography>
              <Textarea
                id="throughputRequired"
                name="throughputRequired"
                rows={3}
                className={styles.inputField}
                value={formData.throughputRequired}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className={styles.formRow}>
            <div>
              <Typography className={styles.inputCaption} variant="label-3" color="greyscale700">
                What latency is required for your solution?
              </Typography>
              <Textarea
                id="latencyRequired"
                name="latencyRequired"
                rows={3}
                className={styles.inputField}
                value={formData.latencyRequired}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className={styles.formRow}>
            <div>
              <Typography className={styles.inputCaption} variant="label-3" color="greyscale700">
                What kind of device are you going to use during the testing?
              </Typography>
              <Textarea
                id="deviceForTesting"
                name="deviceForTesting"
                rows={3}
                className={styles.inputField}
                value={formData.deviceForTesting}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className={styles.formRow}>
            <div>
              <Typography className={styles.inputCaption} variant="label-3" color="greyscale700">
                How many user equipment/SIM cards are needed to do PoC (Proof of Concept)?
              </Typography>
              <Input
                type="number"
                id="simsForPoC"
                name="simsForPoC"
                className={styles.inputField}
                value={formData.simsForPoC}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className={styles.formRow}>
            <div>
              <Typography className={styles.inputCaption} variant="label-3" color="greyscale700">
                Are you able to connect to 5G SA with your testing device?
              </Typography>
              <SelectionDropdown
                inputSize="large"
                className={styles.inputField}
                value={formData.sa5G}
                isOpen={isSa5gOpen}
                onChange={(e) => setFormData((prev) => ({ ...prev, sa5G: e as "Yes" | "No" | "Not Sure" }))}
                onFocus={() => setIsSa5gOpen((prev) => !prev)}
                onClose={() => setIsSa5gOpen(false)}
                options={[
                  { label: "Yes", value: "Yes" },
                  { label: "No", value: "No" },
                  { label: "Not Sure", value: "Not Sure" }
                ]}
              />
            </div>
          </div>

          <div className={styles.formRow}>
            <div>
              <Typography className={styles.inputCaption} variant="label-3" color="greyscale700">
                Are you able to test with a data-centric device?
              </Typography>
              <SelectionDropdown
                inputSize="large"
                className={styles.inputField}
                value={formData.testWithDataSentric}
                isOpen={isTestWithDataSentricOpen}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, testWithDataSentric: e as "Yes" | "No" | "Not Sure" }))
                }
                onFocus={() => setIsTestWithDataSentricOpen((prev) => !prev)}
                onClose={() => setIsTestWithDataSentricOpen(false)}
                options={[
                  { label: "Yes", value: "Yes" },
                  { label: "No", value: "No" },
                  { label: "Not Sure", value: "Not Sure" }
                ]}
              />
            </div>
          </div>

          <div className={styles.formRow}>
            <div>
              <Typography className={styles.inputCaption} variant="label-3" color="greyscale700">
                Does your solution require connection to external cloud services?
              </Typography>
              <SelectionDropdown
                inputSize="large"
                className={styles.inputField}
                value={formData.externalCloud}
                isOpen={isExternalCloud}
                onChange={(e) => setFormData((prev) => ({ ...prev, externalCloud: e as "Yes" | "No" | "Not Sure" }))}
                onFocus={() => setIsExternalCloud((prev) => !prev)}
                onClose={() => setIsExternalCloud(false)}
                options={[
                  { label: "Yes", value: "Yes" },
                  { label: "No", value: "No" },
                  { label: "Not Sure", value: "Not Sure" }
                ]}
              />
            </div>
          </div>

          <div className={styles.formRow}>
            <div>
              <Typography className={styles.inputCaption} variant="label-3" color="greyscale700">
                Team Size
              </Typography>
              <Input
                type="number"
                id="teamSize"
                name="teamSize"
                className={styles.inputField}
                value={formData.teamSize}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className={styles.formRow}>
            <div>
              <Typography className={styles.inputCaption} variant="label-3" color="greyscale700">
                Technology Readiness Level
              </Typography>
              <Input
                type="text"
                id="technologyReadiness"
                name="technologyReadiness"
                className={styles.inputField}
                value={formData.technologyReadiness}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className={styles.formRow}>
            <div>
              <Typography className={styles.inputCaption} variant="label-3" color="greyscale700">
                What are your expectations towards working with us?
              </Typography>
              <Textarea
                id="expectations"
                name="expectations"
                rows={3}
                className={styles.inputField}
                value={formData.expectations}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className={styles.formRow}>
            <div>
              <Typography className={styles.inputCaption} variant="label-3" color="greyscale700">
                How could your product be supercharged with Network APIs?
              </Typography>
              <Textarea
                id="superchargedAnswer"
                name="superchargedAnswer"
                rows={3}
                className={styles.inputField}
                value={formData.superchargedAnswer}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className={styles.formRow}>
            <div>
              <Typography className={styles.inputCaption} variant="label-3" color="greyscale700">
                What would you like to build and test during the challenge?
              </Typography>
              <Textarea
                id="buildAndTestAnswer"
                name="buildAndTestAnswer"
                rows={3}
                className={styles.inputField}
                value={formData.buildAndTestAnswer}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className={styles.formRow}>
            <div>
              <Typography className={styles.inputCaption} variant="label-3" color="greyscale700">
                What would you like to get out of this challenge?
              </Typography>
              <Textarea
                id="getOutOfChallengeAnswer"
                name="getOutOfChallengeAnswer"
                rows={3}
                className={styles.inputField}
                value={formData.getOutOfChallengeAnswer}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className={styles.formRow}>
            <div>
              <Typography className={styles.inputCaption} variant="label-3" color="greyscale700">
                Elaborate on your use-case proposition, combining your product and network APIs
              </Typography>
              <Textarea
                id="useCasePropositionAnswer"
                name="useCasePropositionAnswer"
                rows={3}
                className={styles.inputField}
                value={formData.useCasePropositionAnswer}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className={styles.formRow}>
            <div>
              <Typography className={styles.inputCaption} variant="label-3" color="greyscale700">
                To whom do you deliver the most value with your product/service?
              </Typography>
              <Textarea
                id="deliverTheMostValueAnswer"
                name="deliverTheMostValueAnswer"
                rows={3}
                className={styles.inputField}
                value={formData.deliverTheMostValueAnswer}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className={styles.formRow}>
            <div>
              <Typography className={styles.inputCaption} variant="label-3" color="greyscale700">
                What makes your company unique in the market?
              </Typography>
              <Textarea
                id="makesUniqueAnswer"
                name="makesUniqueAnswer"
                rows={3}
                className={styles.inputField}
                value={formData.makesUniqueAnswer}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className={styles.formRow}>
            <div>
              <Typography className={styles.inputCaption} variant="label-3" color="greyscale700">
                How is your company differentiated from competitors?
              </Typography>
              <Textarea
                id="differentiatedAnswer"
                name="differentiatedAnswer"
                rows={3}
                className={styles.inputField}
                value={formData.differentiatedAnswer}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className={styles.formRow}>
            <div>
              <Typography className={styles.inputCaption} variant="label-3" color="greyscale700">
                What are the most important features and capabilities of your solution?
              </Typography>
              <Textarea
                id="importantFeaturesAnswer"
                name="importantFeaturesAnswer"
                rows={3}
                className={styles.inputField}
                value={formData.importantFeaturesAnswer}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className={styles.formRow}>
            <div>
              <Typography className={styles.inputCaption} variant="label-3" color="greyscale700">
                Upload any additional documents
              </Typography>

              <div className={styles.horizontalLine} />

              {formData.files.length > 0 ? (
                <div>
                  {formData.files.map((f, id) => {
                    const key = `${f.filename}-${id}`;

                    const removeFile = () => {
                      const _files = formData.files.filter((_, _id) => _id !== id);
                      setFormData((prev) => ({ ...prev, files: _files }));
                    };

                    return (
                      <div className={styles.fileRow}>
                        <div>
                          <Typography key={key}>{`${f.filename} (${f.filesize})`}</Typography>
                        </div>
                        <div>
                          <XIcon onClick={removeFile} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <>
                  <input
                    multiple
                    type="file"
                    accept=".jpg,.jpeg,image/jpeg,.png,image/png,.pdf,application/pdf,.ppt,application/vnd.ms-powerpoint,.pptx,application/vnd.openxmlformats-officedocument.presentationml.presentation,.doc,application/msword,.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.txt,text/plain,.xls,application/vnd.ms-excel,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    id="file-upload"
                    ref={fileInputRef}
                    className={styles.hidden}
                    onChange={handleInputChange}
                  />
                  <label htmlFor="file-upload" className={styles.UploadLabel}>
                    <NormalButton
                      onClick={handleButtonClick}
                      className={styles.uploadButton}
                      size="medium"
                      isSubmitting={mutation.isPending}
                      variant="secondary"
                    >
                      Upload Documents
                    </NormalButton>
                  </label>
                </>
              )}
            </div>
          </div>

          <div className={styles.formRow}>
            <div>
              <Typography className={styles.inputCaption} variant="label-3" color="greyscale700">
                Where did you learn about the challenge?
              </Typography>
              <Textarea
                id="whereDidYouLearnAnswer"
                name="whereDidYouLearnAnswer"
                rows={3}
                className={styles.inputField}
                value={formData.whereDidYouLearnAnswer}
                onChange={handleChange}
              />
            </div>
          </div>

          <NormalButton size="large" isDisabled={mutation.isPending} onClick={submitApplication}>
            {mutation.isPending ? "Submitting..." : "Submit Application"}
          </NormalButton>
        </div>
      </div>
    </div>
  );
};
